import React, { useState, useRef, useEffect } from 'react';

import { Divider, Flex, useOutsideClick } from '@chakra-ui/react';
import { useSelector } from 'react-redux';
import useOneOnOnePageParams from 'screens/oneOnOneRabbit/hooks/useOneOnOnePageParams';
import { selectLoggedInEmployeeData } from 'selectors/loggedInEmployee';

import { sendClickedOnAddCustomAgendaEvent } from 'constants/mixpanelHelper';

import { AutoResizeInput } from 'uiToolkitV1/AutoResizeInput';

import { OneOnOneAgendaCreateOrUpdateI, useCreateAgendaMutation } from '../hooks/dataHooks';

export const createAgendaObject = ({
  agendaText,
  displayName,
  profilePhoto,
  userId,
}: {
  agendaText: string;
  displayName: string;
  profilePhoto: string;
  userId: string;
}): OneOnOneAgendaCreateOrUpdateI => ({
  agendaText,
  isCompleted: false,
  isOwnerAdmin: false,
  ownerDetails: {
    id: Number(userId),
    displayName,
    profilePhoto,
  },
  privateNotes: null,
  referenceType: null,
  sharedNotes: null,
});

interface Props {
  setCreatingAgenda: (creatingAgenda: boolean) => void;
}

const CreateAgenda = ({ setCreatingAgenda }: Props) => {
  const { masterId, instanceId } = useOneOnOnePageParams();

  const createAgendaMutation = useCreateAgendaMutation({
    masterId,
    instanceId,
  });

  const loggedInEmployeeData = useSelector(selectLoggedInEmployeeData);

  const inputRef = useRef<HTMLTextAreaElement>(null);

  const [agendaValue, setAgendaValue] = useState('');

  const createAgendaHandler = async (agendaText: string) => {
    if (!agendaText || agendaText.trim() === '') {
      setCreatingAgenda(false);
      return;
    }
    try {
      const newAgenda = createAgendaObject({
        agendaText,
        displayName: loggedInEmployeeData?.displayName,
        profilePhoto: loggedInEmployeeData?.profilePhotoSrc,
        userId: loggedInEmployeeData?.id,
      });
      createAgendaMutation.mutate({
        updatedAgenda: newAgenda,
        onMutation: () => setCreatingAgenda(false),
      });
    } catch (e) {
      console.error('Error creating agenda : ', e);
    } finally {
      // setCreatingAgenda(false);
    }
  };

  const handleAgendaChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const inputValue = e.target.value.slice(0, 255);
    setAgendaValue(inputValue);
  };

  useOutsideClick({
    ref: inputRef,
    handler: () => createAgendaHandler(agendaValue),
  });

  const onEnterPress = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter') {
      sendClickedOnAddCustomAgendaEvent({
        eventProperties: {
          instance_id: instanceId,
          type: 'Custom',
          source: 'by participant',
        },
      });
      createAgendaHandler(agendaValue);
    }
  };

  //   focus on input when component mounts
  useEffect(() => {
    if (inputRef.current) {
      inputRef.current.focus();
    }
  }, []);

  return (
    <>
      <Flex
        p="18px 24px"
        // sx={{
        //   backgroundColor: '#F6FAFE'
        // }}
        w="100%"
        height="78px"
        alignItems="center"
      >
        <AutoResizeInput
          ref={inputRef}
          isDashed
          value={agendaValue}
          onChange={handleAgendaChange}
          onKeyDown={onEnterPress}
          data-testid="create-agenda-input"
        />
      </Flex>
      <Divider opacity={1} color="#EBEFF3" />
    </>
  );
};

export { CreateAgenda };
